import React, {useCallback, useContext, useState} from 'react';

import {FormInput} from 'src/components/input';
import {SaveWithReasonButton} from 'src/components/save-with-reason/save-with-reason-button';
import {SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';

import Button from '@pb/kit-component-button';
import {Dialog, DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {FieldDescription, Types as FieldDescriptionTypes} from '@pb/kit-component-field-description';
import {Group, Item} from '@pb/kit-component-group';

import type {SaveOptions, SaveWithReason} from 'src/components/save-with-reason/save-with-reason-button';

const MODAL_WIDTH = 440;
const DESCRIPTION = 'ID of the user in the PitchBook Salesforce environment. Leave the field empty to remove the ID.';

type Props = {
    shown: boolean,
    sfdcId: string,
    onClose: () => void,
    onSave: (sfdcId: string, options?: SaveOptions) => Promise<string | undefined>,
};

export const EditSfdcIdModal = ({shown, sfdcId, onClose, onSave}: Props) => {
    const {setSuccessMessage} = useContext(MessageContext);
    const [value, setValue] = useState(sfdcId);

    useEffectWithDeps((prev, current) => {
        if (current && !prev) {
            setValue(sfdcId);
        }
    }, shown);

    const onSaveHandler = useCallback<SaveWithReason>(async options => {
        const error = await onSave(value.trim(), options);
        if (!error) {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
            onClose();
        }
        return error;
    }, [onSave, value, setSuccessMessage, onClose]);

    return (
        <Dialog
            shown={shown}
            setShown={onClose}
            wrapperProps={{style: {width: MODAL_WIDTH}}}>
            <DialogHeader onClose={onClose}>Edit PB Salesforce ID</DialogHeader>
            <DialogMain>
                <div style={{padding: 24}}>
                    <Group alignItems="end">
                        <Item grow={1}>
                            <FormInput
                                width="100%"
                                label="PB Salesforce ID"
                                placeholder="PB Salesforce ID"
                                value={value}
                                onChange={setValue}
                            />
                        </Item>
                        <Button variant="Type.SECONDARY" disabled={!value} onClick={() => setValue('')}>
                            Clear
                        </Button>
                    </Group>
                    <FieldDescription color={FieldDescriptionTypes.DEFAULT}>{DESCRIPTION}</FieldDescription>
                </div>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <SaveWithReasonButton onSave={onSaveHandler} disabled={value.trim() === sfdcId}/>
                    <Button variant="Type.SECONDARY" onClick={onClose}>Cancel</Button>
                </Group>
            </DialogFooter>
        </Dialog>
    );
};
